import React from 'react';
import { Box, Button, ButtonProps, Tooltip, Typography } from '@mui/material';
import QueryBuilderRoundedIcon from '@mui/icons-material/QueryBuilderRounded';

import { buttonSizeSx, cardButtonSx, secondaryButtonSx } from 'theme/buttons';
import { VerdictMark } from 'components/common/Verdict/Verdict';

export const ACTION_SX = buttonSizeSx.sm;

export const SECONDARY_ACTION_SX = {
  ...ACTION_SX,
  ...secondaryButtonSx,
} as const;

export const CARD_SECONDARY_ACTION_SX = {
  ...cardButtonSx,
  ...secondaryButtonSx,
  borderColor: 'text.secondary',
  '&:hover': { ...secondaryButtonSx['&:hover'], borderColor: 'text.secondary' },
} as const;

export const STATUS_LABEL_PROPS = {
  variant: 'body2',
  fontWeight: 500,
} as const;

const STATUS_META_PROPS = {
  variant: 'caption',
  fontWeight: 400,
  lineHeight: 1.43,
} as const;

export const ListActionButton: React.FC<
  ButtonProps & { hint: string; readOnly: boolean }
> = ({ hint, readOnly, ...button }) => (
  <Tooltip
    title={readOnly ? `${hint} — your access is read-only` : hint}
    placement="top"
  >
    <Box component="span" display="inline-flex">
      <Button size="small" disabled={readOnly} {...button} />
    </Box>
  </Tooltip>
);

export const PendingStatus: React.FC<{ label: string; hint: string }> = ({
  label,
  hint,
}) => (
  <Tooltip title={hint} placement="top">
    <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
      <QueryBuilderRoundedIcon
        sx={{ fontSize: '1rem', flexShrink: 0, color: 'text.secondary' }}
      />
      <Typography {...STATUS_LABEL_PROPS} color="text.secondary" noWrap>
        {label}
      </Typography>
    </Box>
  </Tooltip>
);

export const ClosedStatus: React.FC<{
  label: string;
  hint: string;
  reason?: string | null;
  settled?: boolean;
}> = ({ label, hint, reason, settled }) => (
  <Tooltip title={hint} placement="top">
    <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
      {settled && (
        <Box
          display="flex"
          flexShrink={0}
          color="highlight.scoreGreenStrong"
          aria-hidden="true"
        >
          <VerdictMark passed size={16} />
        </Box>
      )}
      <Box display="flex" flexDirection="column" minWidth={0}>
        <Typography
          {...STATUS_LABEL_PROPS}
          color={settled ? 'text.primary' : 'text.secondary'}
          noWrap
        >
          {label}
        </Typography>
        {Boolean(reason) && (
          <Typography {...STATUS_META_PROPS} color="text.lightGrey" noWrap>
            {reason}
          </Typography>
        )}
      </Box>
    </Box>
  </Tooltip>
);
